import { ErrorState } from "$components/error-state";
import type { BulkUploadValidationFragmentFragment } from "$routes/_lib/bulk-operations/__generated__/queries.gql";
import { BulkOperationsPage } from "$routes/_lib/bulk-operations/bulk-operations-page";
import {
  BulkUploadResultFragment,
  BulkUploadValidationFragment,
} from "$routes/_lib/bulk-operations/queries";
import Page from "$routes/price-list/_lib/components/page-layout";
import useCurrencyFormat, {
  useConvertFromPenceFactory,
  useCurrencyUnitNames,
} from "$routes/price-list/_lib/currencies";
import { linkToPriceListHome } from "$routes/price-list/links";
import { gql, useApolloClient } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Navigate, useParams } from "react-router-dom";
import {
  type PriceBulkDownloadQuery,
  type PriceBulkDownloadQueryVariables,
  usePriceBulkUploadMutation,
  useValidatePriceBulkUploadLazyQuery,
} from "./__generated__/page.gql";

const _validateQuery = gql`
  ${BulkUploadValidationFragment}

  query validatePriceBulkUpload($input: PriceBulkUploadInput!) {
    priceListV2 {
      validatePriceBulkUpload(input: $input) {
        ...BulkUploadValidationFragment
      }
    }
  }
`;

const _uploadMutation = gql`
  ${BulkUploadResultFragment}

  mutation priceBulkUpload($input: PriceBulkUploadInput!) {
    priceListV2 {
      priceBulkUpload(input: $input) {
        ...BulkUploadResultFragment
      }
    }
  }
`;

const downloadQuery = gql`
  query priceBulkDownload($input: PriceBulkDownloadInput!) {
    priceListV2 {
      priceBulkDownload(input: $input) {
        csv
        totalCount
      }
    }
  }
`;

export const PriceListBulkImportPage: FC = () => {
  const { id: priceListId } = useParams();
  const client = useApolloClient();
  const currency = useCurrencyFormat();
  const penceToPounds = useConvertFromPenceFactory();
  const { major, subunit, subunitsPerMajor } = useCurrencyUnitNames();

  const [
    validate,
    { data: validateData, loading: validateLoading, error: validateError },
  ] = useValidatePriceBulkUploadLazyQuery({ fetchPolicy: "no-cache" });

  const [
    upload,
    { data: uploadData, loading: uploadLoading, error: uploadError },
  ] = usePriceBulkUploadMutation();

  const formattedValidationResult:
    | BulkUploadValidationFragmentFragment
    | undefined = useMemo(() => {
    const result = validateData?.priceListV2.validatePriceBulkUpload;
    if (!result) return undefined;

    const formatPence = (pence: unknown): unknown => {
      const asNumber =
        typeof pence === "number"
          ? pence
          : typeof pence === "string" &&
              pence.trim() !== "" &&
              !Number.isNaN(Number(pence))
            ? Number(pence)
            : undefined;
      if (asNumber === undefined) return pence;
      const major = penceToPounds(asNumber);
      if (major === undefined) return pence;
      return currency ? currency.format(major) : String(major);
    };

    return {
      ...result,
      items: result.items.map((item) => {
        const breakdown = item.validationBreakdown as
          | Record<string, { value: unknown; lastValue: unknown }>
          | null
          | undefined;
        if (!breakdown?.value) return item;
        return {
          ...item,
          validationBreakdown: {
            ...breakdown,
            value: {
              ...breakdown.value,
              value: formatPence(breakdown.value.value),
              lastValue: formatPence(breakdown.value.lastValue),
            },
          },
        };
      }),
    };
  }, [validateData, currency, penceToPounds]);

  if (!priceListId) {
    return <Navigate to={linkToPriceListHome()} replace />;
  }

  if (validateError || uploadError) {
    return <ErrorState error={validateError || uploadError} />;
  }

  async function downloadCsv() {
    const { data } = await client.query<
      PriceBulkDownloadQuery,
      PriceBulkDownloadQueryVariables
    >({
      query: downloadQuery,
      variables: { input: { priceListId } },
      fetchPolicy: "no-cache",
    });
    return data.priceListV2.priceBulkDownload;
  }

  return (
    <Page>
      <Typography variant="h4">
        <FormattedMessage
          id="priceList.bulkImport.heading"
          defaultMessage="Bulk import prices"
        />
      </Typography>
      <Box marginBottom={2}>
        <Typography variant="h6" component="h2" gutterBottom>
          <FormattedMessage
            id="priceList.bulkImport.steps.title"
            defaultMessage="How to change your prices"
          />
        </Typography>
        <ol style={{ marginTop: 0, paddingLeft: "1.25rem" }}>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.download"
                defaultMessage="Click <b>Download CSV</b>. This gives you a file with all your prices now. <b>Always download a new file each time. Do not use an old file.</b>"
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.open"
                defaultMessage="Open the file in Excel or Google Sheets."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.edit"
                defaultMessage="Change the prices. You can also add new rows."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.save"
                defaultMessage="Save the file. Keep it as a CSV file."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.upload"
                defaultMessage="Choose your saved file below to upload it."
              />
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <FormattedMessage
                id="priceList.bulkImport.steps.review"
                defaultMessage="Look at the preview. If it is correct, click <b>Save</b>."
                values={{ b: (chunks) => <strong>{chunks}</strong> }}
              />
            </Typography>
          </li>
        </ol>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id="bulk-import-rules-header"
            aria-controls="bulk-import-rules-content"
          >
            <Typography variant="body2">
              <strong>
                <FormattedMessage
                  id="priceList.bulkImport.rules.title"
                  defaultMessage="More details: how it works"
                />
              </strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id="bulk-import-rules-content"
            aria-labelledby="bulk-import-rules-header"
          >
            <Box
              component="ul"
              margin={0}
              paddingLeft="1.25rem"
              style={{ listStyleType: "disc" }}
            >
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.row"
                    defaultMessage="Each row is one price."
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.columns"
                    defaultMessage="The file has 5 columns: <b>name</b>, <b>category</b>, <b>tariff</b>, <b>revenueDepartment</b>, <b>value</b>. Fill in all 5."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.match"
                    defaultMessage="We match a row by its <b>name</b> and <b>category</b>. If that name and category already exist, we update the price. If they are new, we add a new item."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.noDelete"
                    defaultMessage="We never delete anything. If you remove a row from the file, that price stays. It is not deleted."
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.noChange"
                    defaultMessage="If you upload the same file with no changes, nothing happens."
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.value"
                    defaultMessage="<b>value</b> is in {subunit}, not {major}. {factor} {subunit} = 1 {major}, so 1 {major} is written as {factor}. Write a whole number, 0 or more, with no decimal point and no commas — the same as the file you downloaded."
                    values={{
                      b: (chunks) => <strong>{chunks}</strong>,
                      major,
                      subunit,
                      factor: subunitsPerMajor,
                    }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.category"
                    defaultMessage="<b>category</b> must be one of: general, inventory, investigation, procedure, custom."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.lookups"
                    defaultMessage="<b>tariff</b> and <b>revenueDepartment</b> must match names that already exist in the system."
                    values={{ b: (chunks) => <strong>{chunks}</strong> }}
                  />
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <FormattedMessage
                    id="priceList.bulkImport.rules.unique"
                    defaultMessage="The same name, category and tariff can only be used once in the file."
                  />
                </Typography>
              </li>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <BulkOperationsPage
        entityName="price-list"
        downloadCsv={downloadCsv}
        triggerCsvValidation={(csv: string) =>
          validate({ variables: { input: { priceListId, csv } } })
        }
        validationResult={formattedValidationResult}
        validationResultLoading={validateLoading}
        triggerCsvBulkImport={(csv: string) =>
          upload({ variables: { input: { priceListId, csv } } })
        }
        bulkImportResult={uploadData?.priceListV2.priceBulkUpload ?? undefined}
        bulkImportResultLoading={uploadLoading}
      />
    </Page>
  );
};
